import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { AuthSplitLayout } from '../components/AuthSplitLayout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useToastStore } from '../stores/toastStore';

const loginSchema = z.object({
  tenantId: z
    .string()
    .min(1, 'Indica el identificador de tenant.')
    .max(64, 'El tenantId no puede superar 64 caracteres.'),
  apiKey: z.string().min(1, 'Indica la API key.'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pushToast = useToastStore((state) => state.push);
  const [submitting, setSubmitting] = useState(false);

  const targetPath = (location.state as LocationState | null)?.from ?? '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, navigate, targetPath]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await login(values);
      pushToast({ tone: 'success', title: 'Bienvenido', description: 'Sesión iniciada.' });
      navigate(targetPath, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setError('apiKey', { message: 'Credenciales inválidas.' });
      } else if (error instanceof ApiError && error.status === 400) {
        setError('tenantId', { message: 'tenantId con formato inválido.' });
      } else {
        const description = error instanceof Error ? error.message : 'Inténtalo de nuevo.';
        pushToast({ tone: 'error', title: 'No se pudo iniciar sesión', description });
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <AuthSplitLayout
      title="Inicia sesión"
      subtitle="Accede con tu tenant y tu API key."
      tagline={
        <>
          Tus documentos, convertidos en <span className="brand-text">trabajo hecho</span>.
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <div className="mb-4">
          <label className="label" htmlFor="tenantId">
            Tenant ID
          </label>
          <input
            id="tenantId"
            type="text"
            autoComplete="username"
            className="input"
            {...register('tenantId')}
          />
          {errors.tenantId !== undefined ? (
            <p className="mt-1 text-sm text-danger">{errors.tenantId.message}</p>
          ) : null}
        </div>

        <div className="mb-6">
          <label className="label" htmlFor="apiKey">
            API key
          </label>
          <input
            id="apiKey"
            type="password"
            autoComplete="current-password"
            className="input"
            {...register('apiKey')}
          />
          {errors.apiKey !== undefined ? (
            <p className="mt-1 text-sm text-danger">{errors.apiKey.message}</p>
          ) : null}
        </div>

        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting ? <LoadingSpinner size="sm" label="Entrando…" /> : 'Entrar'}
        </button>
      </form>

      <p className="mt-6 text-xs text-slate-400">
        El token se mantiene únicamente en memoria. Si refrescas el navegador deberás iniciar sesión de nuevo.
      </p>
    </AuthSplitLayout>
  );
}
